import React, { FC } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import BlankCard from '~components/atoms/BlankCard';
import NavButton, { TNavButtonSubNav } from '~components/atoms/NavButton';
import Image from '~components/atoms/Image';

import { ModuleEnum, NavigationTabsEnum, PermissionsEnum } from '~utils/enums';
import AffinLogo from '~assets/images/affin-logo.webp';

import * as Styles from './_navBar.module.scss';

type TNavBarProps = {
    currentTab: NavigationTabsEnum;
}

const NavBar: FC<TNavBarProps> = ({
    currentTab,
}) => {
    const dispatch = useAppDispatch();
    const setTab = (tab: NavigationTabsEnum) => dispatch(combinedActions.navigationSetCurrentTab(tab));

    const userAccess = useAppSelector(state => state.auth.userDetails.access);

    const viewAccessModules = userAccess.find(access => access.permission === PermissionsEnum.View)?.modules || [];

    const goldManagementSubNav = [
        viewAccessModules.find(module => module === ModuleEnum.BuySellGold) && {
            label: NavigationTabsEnum.GoldManagementBuyAndSellGold,
            onClick: () => setTab(NavigationTabsEnum.GoldManagementBuyAndSellGold),
            isActive: currentTab === NavigationTabsEnum.GoldManagementBuyAndSellGold,
        },
        viewAccessModules.find(module => module === ModuleEnum.Inventory) && {
            label: NavigationTabsEnum.GoldManagementGoldInventory,
            onClick: () => setTab(NavigationTabsEnum.GoldManagementGoldInventory),
            isActive: currentTab === NavigationTabsEnum.GoldManagementGoldInventory,
        },
        // viewAccessModules.find(module => module === ModuleEnum.InventoryTransfer) && {
        //     label: NavigationTabsEnum.GoldManagementGoldInventoryReport,
        //     onClick: () => setTab(NavigationTabsEnum.GoldManagementGoldInventoryReport),
        //     isActive: currentTab === NavigationTabsEnum.GoldManagementGoldInventoryReport,
        // },
        viewAccessModules.find(module => module === ModuleEnum.InventoryTransaction) && {
            label: NavigationTabsEnum.GoldManagementInventoryTransactions,
            onClick: () => setTab(NavigationTabsEnum.GoldManagementInventoryTransactions),
            isActive: currentTab === NavigationTabsEnum.GoldManagementInventoryTransactions,
        },
        viewAccessModules.find(module => module === ModuleEnum.Conversion) && {
            label: NavigationTabsEnum.Conversion,
            onClick: () => setTab(NavigationTabsEnum.Conversion),
            isActive: currentTab === NavigationTabsEnum.Conversion,
        },
        viewAccessModules.find(module => module === ModuleEnum.InventoryTransaction) && {
            label: NavigationTabsEnum.GoldManagementSerialNumberSearch,
            onClick: () => setTab(NavigationTabsEnum.GoldManagementSerialNumberSearch),
            isActive: currentTab === NavigationTabsEnum.GoldManagementSerialNumberSearch,
        },
        viewAccessModules.find(module => module === ModuleEnum.MarketStatus) && {
            label: NavigationTabsEnum.MarketStatus,
            onClick: () => setTab(NavigationTabsEnum.MarketStatus),
            isActive: currentTab === NavigationTabsEnum.MarketStatus,
        },
        viewAccessModules.find(module => module === ModuleEnum.InventoryStockOut) && {
            label: NavigationTabsEnum.StockOut,
            onClick: () => setTab(NavigationTabsEnum.StockOut),
            isActive: currentTab === NavigationTabsEnum.StockOut,
        },
        viewAccessModules.find(module => module === ModuleEnum.CustomerTransfer) && {
            label: NavigationTabsEnum.CustomerTransfer,
            onClick: () => setTab(NavigationTabsEnum.CustomerTransfer),
            isActive: currentTab === NavigationTabsEnum.CustomerTransfer,
        },
    ].filter(item => item);

    const productManagementSubNav = [
        viewAccessModules.find(module => module === ModuleEnum.Product) && {
            label: NavigationTabsEnum.SplitSettings,
            onClick: () => setTab(NavigationTabsEnum.SplitSettings),
            isActive: currentTab === NavigationTabsEnum.SplitSettings,
        },
        viewAccessModules.find(module => module === ModuleEnum.Discount) && {
            label: NavigationTabsEnum.Discount,
            onClick: () => setTab(NavigationTabsEnum.Discount),
            isActive: currentTab === NavigationTabsEnum.Discount,
        },
    ].filter(item => item);

    const reportSubNav = [
        viewAccessModules.find(module => module === ModuleEnum.SalesReport) && {
            label: NavigationTabsEnum.ReportSalesReport,
            onClick: () => setTab(NavigationTabsEnum.ReportSalesReport),
            isActive: currentTab === NavigationTabsEnum.ReportSalesReport,
        },
        viewAccessModules.find(module => module === ModuleEnum.InventoryReport) && {
            label: NavigationTabsEnum.ReportInventoryReport,
            onClick: () => setTab(NavigationTabsEnum.ReportInventoryReport),
            isActive: currentTab === NavigationTabsEnum.ReportInventoryReport,
        },
    ].filter(item => item);

    return (
        <BlankCard containerStyle={Styles.container}>
            <div className={Styles.subcontainer}>
                <div className={Styles['nav-button-container']}>
                    <Image
                        src={AffinLogo}
                        width='auto'
                        height={40}
                        style={{ margin: '10px 0px' }}
                    />

                    {viewAccessModules.find(module => module === ModuleEnum.Dashboard)
                        &&
                        <NavButton
                            label={NavigationTabsEnum.Dashboard}
                            onClick={() => setTab(NavigationTabsEnum.Dashboard)}
                            isActive={currentTab === NavigationTabsEnum.Dashboard}
                        />
                    }

                    {viewAccessModules.find(module => module === ModuleEnum.CustomerManagement)
                        &&
                        <NavButton
                            label={NavigationTabsEnum.CustomerManagement}
                            onClick={() => setTab(NavigationTabsEnum.CustomerManagement)}
                            isActive={currentTab === NavigationTabsEnum.CustomerManagement}
                        />
                    }

                    {viewAccessModules.find(module => {
                        return (
                            module === ModuleEnum.BuySellGold
                            || module === ModuleEnum.Inventory
                            || module === ModuleEnum.InventoryTransaction
                            || module === ModuleEnum.InventoryTransfer
                            || module === ModuleEnum.MarketStatus
                            || module === ModuleEnum.Conversion
                            || module === ModuleEnum.InventoryStockOut
                            || module === ModuleEnum.CustomerTransfer
                        );
                    }) &&
                        <NavButton
                            label={NavigationTabsEnum.GoldManagement}
                            subNav={goldManagementSubNav as TNavButtonSubNav}
                        />
                    }

                    {viewAccessModules.find(module => module === ModuleEnum.Project)
                        &&
                        <NavButton
                            label={NavigationTabsEnum.ProjectManagement}
                            onClick={() => setTab(NavigationTabsEnum.ProjectManagement)}
                            isActive={currentTab === NavigationTabsEnum.ProjectManagement}
                        />
                    }

                    {viewAccessModules.find(module => module === ModuleEnum.Approval)
                        &&
                        <NavButton
                            label={NavigationTabsEnum.Approval}
                            onClick={() => setTab(NavigationTabsEnum.Approval)}
                            isActive={currentTab === NavigationTabsEnum.Approval}
                        />
                    }

                    {viewAccessModules.find(module => {
                        return (
                            module === ModuleEnum.Product
                            || module === ModuleEnum.Discount
                        );
                    }) &&
                        <NavButton
                            label={NavigationTabsEnum.ProductManagement}
                            subNav={productManagementSubNav as TNavButtonSubNav}
                        />
                    }

                    {viewAccessModules.find(module => module === ModuleEnum.BranchManagement)
                        &&
                        <NavButton
                            label={NavigationTabsEnum.BranchManagement}
                            subNav={[
                                {
                                    label: NavigationTabsEnum.BranchManagementRegions,
                                    onClick: () => setTab(NavigationTabsEnum.BranchManagementRegions),
                                    isActive: currentTab === NavigationTabsEnum.BranchManagementRegions,
                                },
                                {
                                    label: NavigationTabsEnum.BranchManagementBranches,
                                    onClick: () => setTab(NavigationTabsEnum.BranchManagementBranches),
                                    isActive: currentTab === NavigationTabsEnum.BranchManagementBranches,
                                },
                            ]}
                        />
                    }

                    {viewAccessModules.find(module => module === ModuleEnum.RBAC)
                        &&
                        <NavButton
                            label={NavigationTabsEnum.AdminManagement}
                            subNav={[
                                {
                                    label: NavigationTabsEnum.AdminManagementRoles,
                                    onClick: () => setTab(NavigationTabsEnum.AdminManagementRoles),
                                    isActive: currentTab === NavigationTabsEnum.AdminManagementRoles,
                                },
                                {
                                    label: NavigationTabsEnum.AdminManagementAdmins,
                                    onClick: () => setTab(NavigationTabsEnum.AdminManagementAdmins),
                                    isActive: currentTab === NavigationTabsEnum.AdminManagementAdmins,
                                },
                            ]}
                        />
                    }

                    {viewAccessModules.find(module => module === ModuleEnum.Setting)
                        &&
                        <NavButton
                            label={NavigationTabsEnum.Settings}
                            onClick={() => setTab(NavigationTabsEnum.Settings)}
                            isActive={currentTab === NavigationTabsEnum.Settings}
                        />
                    }

                    {viewAccessModules.find(module => {
                        return (
                            module === ModuleEnum.SalesReport
                            || module === ModuleEnum.InventoryReport
                        );
                    }) &&
                        <NavButton
                            label={NavigationTabsEnum.Report}
                            subNav={reportSubNav as TNavButtonSubNav}
                        />
                    }
                </div>
            </div>
        </BlankCard>
    );
};

export default NavBar;
